// 📄 src/hooks/useSignalR.js
import { useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { toast } from "react-toastify";
import { getAuthGeneration, TOKEN_KEY } from "../api/axiosClient"; // single source of truth

const REALTIME_TOAST_ID = "signalr-realtime-failed";
const activeConnections = new Set();

export function stopAllSignalRConnections() {
  const connections = Array.from(activeConnections);
  activeConnections.clear();

  connections.forEach(conn => {
    if (!conn || conn.state === signalR.HubConnectionState.Disconnected) {
      return;
    }

    try {
      conn.stop?.().catch?.(() => {});
    } catch {
      // Logout cleanup must stay best-effort.
    }
  });
}

function getHubUrl() {
  const raw =
    (process.env.REACT_APP_API_BASE_URL &&
      process.env.REACT_APP_API_BASE_URL.trim()) ||
    "http://localhost:7113/api";

  const base = raw.replace(/\/+$/, "");
  return `${base}/hubs/inbox`; // => .../api/hubs/inbox
}

function isIgnorableSignalRError(err) {
  const name = String(err?.name || "");
  const msg = String(err?.message || err || "").toLowerCase();

  return (
    name === "AbortError" ||
    msg.includes("stopped during negotiation") ||
    msg.includes("abort") ||
    msg.includes("canceled") ||
    msg.includes("cancelled")
  );
}

function isCurrentSignalRSession(connectionGeneration) {
  return (
    connectionGeneration === getAuthGeneration() &&
    !!localStorage.getItem(TOKEN_KEY)
  );
}

export default function useSignalR({
  onMessageReceived,
  onUnreadChanged,
} = {}) {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const connRef = useRef(null);
  const mountedRef = useRef(false);
  const startingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    const hubUrl = getHubUrl();
    const token = localStorage.getItem(TOKEN_KEY);
    const connectionGeneration = getAuthGeneration();

    if (!token) {
      console.warn("SignalR skipped: No auth token found.");
      setIsConnected(false);
      return () => {
        mountedRef.current = false;
      };
    }

    // ✅ Build connection
    // Append SA business context if selected
    let finalUrl = hubUrl;
    const saBizId = localStorage.getItem("sa_selectedBusinessId");
    if (saBizId) {
      finalUrl += `?bizId=${encodeURIComponent(saBizId)}`;
    }

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(finalUrl, {
        accessTokenFactory: () => localStorage.getItem(TOKEN_KEY) || "",
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connRef.current = conn;
    activeConnections.add(conn);
    setConnection(conn);

    // ✅ Subscribe before start
    const handleMessageReceived = (...args) => {
      if (!isCurrentSignalRSession(connectionGeneration)) return;
      onMessageReceived?.(...args);
    };

    const handleUnreadChanged = (...args) => {
      if (!isCurrentSignalRSession(connectionGeneration)) return;
      onUnreadChanged?.(...args);
    };

    if (onMessageReceived) conn.on("ReceiveInboxMessage", handleMessageReceived);
    if (onUnreadChanged) conn.on("UnreadCountChanged", handleUnreadChanged);

    conn.onreconnecting(() => {
      if (!mountedRef.current || !isCurrentSignalRSession(connectionGeneration)) return;
      setIsConnected(false);
    });

    conn.onreconnected(() => {
      if (!mountedRef.current || !isCurrentSignalRSession(connectionGeneration)) return;
      setIsConnected(true);
      toast.dismiss(REALTIME_TOAST_ID);
    });

    conn.onclose(err => {
      activeConnections.delete(conn);
      if (!mountedRef.current || !isCurrentSignalRSession(connectionGeneration)) return;
      setIsConnected(false);

      // Ignore dev-mode abort noise
      if (isIgnorableSignalRError(err)) return;

      // ✅ Only ONE toast ever
      toast.error("Real-time connection failed.", {
        toastId: REALTIME_TOAST_ID,
      });
    });

    const start = async () => {
      if (startingRef.current) return;
      startingRef.current = true;

      try {
        await conn.start();
        if (!mountedRef.current) return;
        if (!isCurrentSignalRSession(connectionGeneration)) {
          conn.stop().catch(() => {});
          return;
        }

        setIsConnected(true);
        toast.dismiss(REALTIME_TOAST_ID);
        // console.log("✅ SignalR connected:", hubUrl);
        console.log("✅ SignalR connected:", finalUrl);
      } catch (err) {
        if (!mountedRef.current || !isCurrentSignalRSession(connectionGeneration)) return;

        // Ignore dev-mode abort noise
        if (isIgnorableSignalRError(err)) return;

        setIsConnected(false);

        const msg = String(err?.message || err || "").toLowerCase();
        if (msg.includes("401") || msg.includes("unauthorized")) {
          toast.error("SignalR unauthorized. Your session may have expired.", {
            toastId: REALTIME_TOAST_ID,
          });
        } else {
          toast.error("Real-time connection failed.", {
            toastId: REALTIME_TOAST_ID,
          });
        }

        console.error("❌ SignalR start failed:", err);
      } finally {
        startingRef.current = false;
      }
    };

    start();

    return () => {
      mountedRef.current = false;
      activeConnections.delete(conn);

      try {
        if (onMessageReceived)
          conn.off("ReceiveInboxMessage", handleMessageReceived);
        if (onUnreadChanged) conn.off("UnreadCountChanged", handleUnreadChanged);
      } catch {
        // ignore
      }

      if (conn && conn.state !== signalR.HubConnectionState.Disconnected) {
        conn.stop().catch(() => {});
      }
    };
  }, [onMessageReceived, onUnreadChanged]);

  return { connection, isConnected };
}
